import { create } from 'zustand';
import {
  login as authLogin,
  logout as authLogout,
  handleCallback,
  isAuthenticated,
  getCurrentUserFromToken,
  getValidAccessToken,
  type AuthUser,
} from '../services/auth';

/** PMO-specific role assigned by Super Admin */
export type PmoRole = 'super_admin' | 'project_admin' | 'project_resource' | 'read_only';

/** Full user profile combining JWT + PMO-local data */
export interface PmoUser extends AuthUser {
  avatarUrl: string;
  pmo_role: PmoRole;
  assignedProjects: string[];
  pmo_user_id: string;
  /**
   * RBAC permissions the principal actually holds, e.g. `catalog:admin`.
   * Returned by /auth/me and previously dropped on the floor here, which is
   * why the UI could only reason about the four `pmo_role` values — and the
   * CRM ladder is deliberately NOT one of those.
   */
  rbacPermissions: string[];
  /**
   * Modules per organisation, `{orgId: ['crm', 'ways', ...]}`, from /auth/me. Modules are
   * granted per org, so a rail is gated on this and never on the cross-org union.
   */
  modulesByOrg: Record<string, string[]>;
}

interface AuthState {
  user: PmoUser | null;
  loading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  processCallback: (searchParams: URLSearchParams) => Promise<boolean>;
  login: (loginHint?: string) => Promise<void>;
  logout: () => void;
  fetchCurrentUser: () => Promise<void>;

  // Computed helpers
  isAuthenticated: () => boolean;
  hasRole: (...roles: PmoRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasModule: (module: string) => boolean;
  canCreateProject: () => boolean;
  canEditProject: (projectId?: string) => boolean;
  canCreateWorkItem: (projectId?: string) => boolean;
  canEditWorkItem: (projectId?: string) => boolean;
  canDeleteWorkItem: () => boolean;
  canAccessFunding: () => boolean;
  canAccessAdmin: () => boolean;
  canAccessSettings: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  initialize: async () => {
    set({ loading: true, error: null });

    if (!isAuthenticated()) {
      set({ user: null, loading: false });
      return;
    }

    // We have tokens — fetch the full PMO user profile from the backend
    try {
      await get().fetchCurrentUser();
    } catch {
      set({ user: null, loading: false });
    }
  },

  processCallback: async (searchParams) => {
    set({ loading: true, error: null });
    try {
      const success = await handleCallback(searchParams);
      if (success) {
        await get().fetchCurrentUser();
      }
      return success;
    } catch (err: any) {
      set({ error: err.message, loading: false });
      return false;
    }
  },

  login: async (loginHint?: string) => {
    set({ error: null });
    await authLogin(loginHint);
  },

  logout: () => {
    authLogout();
    set({ user: null, error: null });
  },

  fetchCurrentUser: async () => {
    try {
      const token = await getValidAccessToken();
      if (!token) {
        set({ user: null, loading: false });
        return;
      }

      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await res.json();
      const jwtUser = getCurrentUserFromToken();

      const pmoUser: PmoUser = {
        sub: data.sub || jwtUser?.sub || '',
        email: data.email || jwtUser?.email || '',
        name: data.name || jwtUser?.name || '',
        user_type: data.user_type || jwtUser?.user_type || 'internal',
        roles: jwtUser?.roles || [],
        avatarUrl: data.avatarUrl || '',
        pmo_role: data.pmo_role || 'read_only',
        assignedProjects: data.assignedProjects || [],
        pmo_user_id: data.pmo_user_id || '',
        rbacPermissions: data.rbacPermissions || [],
        modulesByOrg: data.modulesByOrg || {},
      };

      set({ user: pmoUser, loading: false, error: null });
    } catch (err: any) {
      set({ user: null, loading: false, error: err.message });
    }
  },

  // ── Computed helpers ────────────────────────────────────────
  isAuthenticated: () => get().user !== null,

  hasRole: (...roles) => {
    const user = get().user;
    if (!user) return false;
    return roles.includes(user.pmo_role);
  },

  /**
   * Does the principal hold an RBAC permission?
   *
   * `super_admin` is a floor, mirroring `require_capability` on the server: the
   * UI must not offer an action the API would refuse, nor hide one it would
   * allow. Grants are not seeded everywhere yet, so without the floor the whole
   * catalogue would look read-only to the people who administer it.
   */
  hasPermission: (permission) => {
    const user = get().user;
    if (!user) return false;
    if (user.pmo_role === 'super_admin') return true;
    return (user.rbacPermissions || []).includes(permission);
  },

  /**
   * Is a module switched on for the principal in any of their organisations? The UI has
   * no current-org selector, so "any org" decides whether the rail shows it; the
   * service behind the module still authorises each request. `super_admin` is the
   * same floor as hasPermission.
   */
  hasModule: (module) => {
    const user = get().user;
    if (!user) return false;
    if (user.pmo_role === 'super_admin') return true;
    return Object.values(user.modulesByOrg || {}).some((mods) => mods.includes(module));
  },

  canCreateProject: () => get().hasRole('super_admin', 'project_admin'),

  canEditProject: (_projectId?: string) => {
    const user = get().user;
    if (!user) return false;
    if (user.pmo_role === 'super_admin' || user.pmo_role === 'project_admin') return true;
    return false;
  },

  canCreateWorkItem: (projectId?: string) => {
    const user = get().user;
    if (!user) return false;
    if (user.pmo_role === 'super_admin' || user.pmo_role === 'project_admin') return true;
    if (user.pmo_role === 'project_resource') {
      if (!projectId) return true; // Allow if no project context
      return user.assignedProjects.includes(projectId);
    }
    return false;
  },

  canEditWorkItem: (projectId?: string) => {
    const user = get().user;
    if (!user) return false;
    if (user.pmo_role === 'super_admin' || user.pmo_role === 'project_admin') return true;
    if (user.pmo_role === 'project_resource') {
      if (!projectId) return true;
      return user.assignedProjects.includes(projectId);
    }
    return false;
  },

  canDeleteWorkItem: () => get().hasRole('super_admin', 'project_admin'),

  canAccessFunding: () => get().hasRole('super_admin', 'project_admin'),

  canAccessAdmin: () => get().hasRole('super_admin'),

  canAccessSettings: () => get().hasRole('super_admin'),
}));
